import React from 'react'
import { Card, Button, Typography, Empty, Dropdown, App, Tag } from 'antd'
import { CalendarOutlined, MoreOutlined, ExclamationCircleOutlined, ArrowRightOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { IDashboardLeaveRequest } from '@vacationtracker/shared/types/leave-request'
import { isToilLeave } from '@vacationtracker/shared/functions/is-toil-leave-request'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { formatTimeString } from '@vacationtracker/shared/functions/get-days-or-hours'
import IntlMessages from '../../../util/IntlMessages'
import { dashboardTracker } from '../services/userDashboardTracking'

const { Title, Text } = Typography

interface UpcomingLeavesCardProps {
  upcomingLeaves: IDashboardLeaveRequest[]
  onRequestLeave: () => void
  onViewAll: () => void
  onViewProfile: () => void
  onCancelLeave?: (leaveId: string) => void
  isLoading?: boolean
  className?: string
}

const UpcomingLeavesCardComponent: React.FC<UpcomingLeavesCardProps> = ({ upcomingLeaves, onRequestLeave, onViewProfile, onCancelLeave, className = '' }) => {
  const { formatMessage } = useIntl()
  const { modal } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'status-approved'
      case 'PENDING':
        return 'status-pending'
      case 'REJECTED':
        return 'status-rejected'
      default:
        return 'status-default'
    }
  }

  const getStatusDisplayText = (status: string) => {
    switch (status) {
      case 'OPEN':
        return formatMessage({ id: 'app.pending' })
      case 'APPROVED':
        return formatMessage({ id: 'app.approved' })
      default:
        return status
    }
  }

  const formatLeaveDate = (leave: IDashboardLeaveRequest) => {
    const start = dayjs(leave.startDate)
    const end = dayjs(leave.endDate)
    const userHourFormat = authUser?.hourFormat || HourFormatEnum.twentyFour

    // Helper to format time for part-day leaves
    const formatPartDayTime = () => {
      if (!leave.isPartDay) return ''

      // Use the new partDay object if available, otherwise fall back to legacy fields
      const startHour = leave.partDay?.startHour ?? leave.partDayStartHour
      const startMinute = leave.partDay?.startMinute ?? 0
      const endHour = leave.partDay?.endHour ?? leave.partDayEndHour
      const endMinute = leave.partDay?.endMinute ?? 0

      if (startHour == null || endHour == null) return ''

      const startTime = formatTimeString({
        hour: startHour,
        minute: startMinute,
        showZeroPadding: false, // Don't show :00 for whole hours
        hourFormat: userHourFormat,
        amOrPm: userHourFormat === HourFormatEnum.twelve ? (startHour >= 12 ? 'PM' : 'AM') : undefined,
      })

      const endTime = formatTimeString({
        hour: endHour,
        minute: endMinute,
        showZeroPadding: false, // Don't show :00 for whole hours
        hourFormat: userHourFormat,
        amOrPm: userHourFormat === HourFormatEnum.twelve ? (endHour >= 12 ? 'PM' : 'AM') : undefined,
      })

      return ` - ${startTime} to ${endTime}`
    }

    if (start.isSame(end, 'day')) {
      // Single day: "Mon, Aug 25" or "Mon, Aug 25 - 10:00 to 14:00"
      return `${start.format('ddd, MMM DD')}${formatPartDayTime()}`
    } else {
      // Multiple days: "Mon, Aug 25 - Wed, Aug 27"
      // Note: Part-day times don't make sense for multi-day leaves
      return `${start.format('ddd, MMM DD')} - ${end.format('ddd, MMM DD')}`
    }
  }

  const handleCancelLeave = (leave: IDashboardLeaveRequest) => {
    modal.confirm({
      title: formatMessage({ id: 'app.cancelLeave' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'app.cancelLeaveConfirmText' }),
      okText: formatMessage({ id: 'app.yes' }),
      okType: 'primary',
      cancelText: formatMessage({ id: 'app.no' }),
      onOk() {
        onCancelLeave?.(leave.id)
      },
    })
  }

  const getLeaveActionsMenu = (leave: IDashboardLeaveRequest) => {
    const isToil = isToilLeave(leave.id)
    const isEditDisabled = leave.approvedLevel === ApproversLevelEnum.FIRST

    return {
      items: [
        {
          key: 'edit',
          label: isEditDisabled ? (
            <span style={{ color: '#d9d9d9' }}>
              <IntlMessages id="app.edit" />
            </span>
          ) : (
            <Link to={isToil ? `/app/leaves/toil/${leave.id}/edit` : `/app/leaves/${leave.id}/edit`}>
              <IntlMessages id="app.edit" />
            </Link>
          ),
          disabled: isEditDisabled,
        },
        {
          key: 'cancel',
          label: <IntlMessages id="app.cancel" />,
          onClick: () => handleCancelLeave(leave),
        },
      ],
    }
  }

  // Show maximum 3 upcoming leaves
  const displayLeaves = upcomingLeaves.slice(0, 3)

  return (
    <Card
      className={`upcoming-leaves-card ${className}`}
      title={
        <div className="card-header-content">
          <div className="header-title-section">
            <CalendarOutlined className="header-icon" />
            <Title level={4} className="header-title">
              {formatMessage({ id: 'dashboard.upcomingLeaves.title' })}
            </Title>
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => {
              dashboardTracker.trackActionTaken({
                actionType: 'button_click',
                actionName: 'see_all_upcoming_leaves',
                elementType: 'button',
                section: 'upcoming_leaves',
                currentLeaveType: 'unknown',
                currentBalance: 0,
                ledToNavigation: true,
                destinationUrl: '/app/my-profile',
              })
              onViewProfile()
            }}
            className="see-all-button"
          >
            {formatMessage({ id: 'dashboard.seeAll' })} <ArrowRightOutlined />
          </Button>
        </div>
      }
    >
      {displayLeaves.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div className="empty-state">
              <Text type="secondary">{formatMessage({ id: 'dashboard.upcomingLeaves.empty' })}</Text>
              <br />
              <Button
                type="primary"
                onClick={() => {
                  dashboardTracker.trackActionTaken({
                    actionType: 'button_click',
                    actionName: 'request_leave_empty_state',
                    elementType: 'button',
                    section: 'upcoming_leaves',
                    currentLeaveType: 'unknown',
                    currentBalance: 0,
                    ledToNavigation: true,
                    destinationUrl: '/app/request-leave',
                  })
                  onRequestLeave()
                }}
                className="empty-button"
              >
                {formatMessage({ id: 'app.requestLeave' })}
              </Button>
            </div>
          }
        />
      ) : (
        <>
          <div className="leaves-container">
            {displayLeaves.map((leave) => (
              <div key={leave.id} className="leave-item">
                {/* Date box */}
                <div className="date-box">
                  <div className="date-day">{dayjs(leave.startDate).format('DD')}</div>
                  <div className="date-month">{dayjs(leave.startDate).format('MMM')}</div>
                </div>

                {/* Leave details */}
                <div className="leave-details">
                  <div className="leave-type-name">
                    {(leave as any).leaveType?.name || leave.leavePolicy?.leaveType?.name || formatMessage({ id: 'app.leave' })}
                    {isToilLeave(leave.id) && (
                      <Tag className="toil-tag" style={{ marginLeft: 8 }} color={'green'}>
                        {formatMessage({ id: 'app.toil' })}
                      </Tag>
                    )}
                  </div>
                  <div className="leave-dates">{formatLeaveDate(leave)}</div>
                </div>

                {/* Status badge and actions */}
                <div className="leave-actions">
                  <span className={`status-badge ${getStatusBadgeClass(leave.status)}`}>{getStatusDisplayText(leave.status)}</span>

                  {/* Actions menu - only show for non-denied leaves */}
                  {leave.status !== 'DENIED' && leave.status !== 'CANCELLED' && onCancelLeave && (
                    <Dropdown menu={getLeaveActionsMenu(leave)} trigger={['click']} placement="bottomRight">
                      <Button type="text" size="small" icon={<MoreOutlined />} className="actions-menu-button" />
                    </Dropdown>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer with count */}
          <div className="footer">
            <span>
              {formatMessage(
                { id: 'dashboard.upcomingLeaves.showingCount' },
                {
                  showing: Math.min(displayLeaves.length, 3),
                  total: upcomingLeaves.length,
                }
              )}
            </span>
          </div>
        </>
      )}
    </Card>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when upcoming leaves data or callbacks change
const UpcomingLeavesCard = React.memo(UpcomingLeavesCardComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  return (
    // Check array reference - assumes immutable updates
    prevProps.upcomingLeaves === nextProps.upcomingLeaves &&
    prevProps.className === nextProps.className &&
    prevProps.isLoading === nextProps.isLoading &&
    // Function references - assumes stable references from parent
    prevProps.onRequestLeave === nextProps.onRequestLeave &&
    prevProps.onViewProfile === nextProps.onViewProfile &&
    prevProps.onCancelLeave === nextProps.onCancelLeave
  )
})

// Set display name for debugging
UpcomingLeavesCard.displayName = 'UpcomingLeavesCard'

export default UpcomingLeavesCard
